/** /bookings/ — ops-wide booking search across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array.
 */
import type { SuggestionFetcher, WireNode } from '@/components/QuerySearch';

import { apiGet, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { ApiRequestListItem } from './apiRequests';
import type { BookingRecordCheckinStatus, BookingRecordPrecheckinState } from './bookingRecord';
import type { ImportRunListItem } from './importRuns';
import type { PaginatedResponse } from './users';
import type { AiDiagStartBody, DiagnoseLaunchContext } from '@/features/ai-diag/types';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface BookingTenantRef {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  /** Set when this tenant is a franchise licensee (child TO). */
  parent_id: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface BookingTourRef {
  id: number;
  name: string;
  /** Staff-only alias of the tour; null when unset. */
  internal_name: string | null;
  /** A one-off tour: its bookings share the tour's own schedule. */
  is_bespoke: boolean;
  /** Group_Tour.trip_type: 0 = tour, 1 = bus trip, 2 = exhibition. */
  trip_type: 0 | 1 | 2;
  trip_type_display: string;
}

export interface BookingGuest {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
}

export interface BookingDepartureRef {
  id: number;
  name: string;
}

export interface BookingTagCategory {
  id: number;
  name: string;
  code: string;
}

export interface BookingTag {
  id: number;
  name: string;
  category: BookingTagCategory;
}

/** The staff role on a booking (Group_Tour_User.role -> Employee_Role). */
export interface BookingRoleRef {
  id: number;
  name: string;
  /** `guide` when the role's permissions include broadcasting the user position. */
  kind: 'guide' | 'staff';
}

/** Where a booking stands in time now — the trip spine's rule, the same buckets as
 *  the `phase` filter namespace (features/records/time/tripPhase). */
export type BookingTripPhase = 'past' | 'active' | 'future' | 'undated';

/** Who the person is on this booking: `guide` = a role that broadcasts the user
 *  position; `staff` = any other role, or a staff record in the tour's tenant. */
export type BookingParticipantKind = 'guest' | 'guide' | 'staff' | 'observer';

export interface BookingPersonRef {
  id: number;
  first_name: string | null;
  last_name: string | null;
}

/** `expand=guides` — who works the departure and who is assigned to the booking. */
export interface BookingGuides {
  /** The departure's other staff bookings, guides first; at most 10. */
  staff: Array<{
    booking_id: number;
    user: BookingPersonRef;
    role: { id: number; name: string; kind: 'guide' | 'staff' };
  }>;
  /** Staff beyond the listed 10. */
  staff_more: number;
  /** The booking's tags that name a person (guide / coordinator / van / bus / resource). */
  assigned: Array<{
    tag: { id: number; name: string };
    category: { id: number; code: string; name: string } | null;
    user: BookingPersonRef;
  }>;
  /** Staff rows only: the departure's guest bookings. Null on guest / observer rows
   *  and when the departure is unknown. */
  guest_count: number | null;
}

/** A place a booking starts from, with the source that named it. */
export interface BookingPlace {
  name: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  source: 'pickup' | 'meeting_point' | 'tour_meeting_point' | 'first_activity';
}

/** `expand=where` — where the booking starts and where the trip goes. */
export interface BookingWhere {
  start: BookingPlace | null;
  /** At most 6 stops; `route_more` counts the rest. */
  route: Array<{ name: string; country: string | null }>;
  route_more: number;
  /** Null when there is no route (most booking-only integrations never send one). */
  route_source: 'trip_flow' | 'tour_tags' | null;
}

export interface BookingInstantRange {
  from: string | null;
  to: string | null;
}

export type BookingPublishReason =
  | 'not_booked'
  | 'tour_hidden'
  | 'booking_hidden'
  | 'self_registration'
  | 'guide_group'
  | 'user_exempt'
  | 'guest'
  | 'no_role'
  | 'role_not_windowed'
  | 'no_start'
  | 'bespoke_upcoming'
  | 'before_window'
  | 'in_window'
  | 'no_end'
  | 'after_window';

/** `expand=staff_visibility` (staff rows only) — is the booking in front of the guide
 *  in the guide app: published to the cache AND now inside the app window. */
export interface BookingStaffVisibility {
  /** The publish gate, evaluated now. */
  publish: { visible: boolean; reason: BookingPublishReason; window: BookingInstantRange };
  /** The manifest's visible_between widened by the tenant's deltas; null when the
   *  manifest ships no hint. */
  app_window: BookingInstantRange | null;
  /** What the guide's cache holds now. */
  cache: {
    state: 'published' | 'tombstoned' | 'missing';
    valid_from: string | null;
    valid_to: string | null;
  };
}

export type BookingChatState =
  | 'in_chat'
  | 'permanent'
  | 'no_channel'
  | 'archived'
  | 'not_booked'
  | 'joins'
  | 'left'
  | 'excluded'
  | 'not_placed';

/** `expand=chat` (every row) — the person's membership in the trip's chat, from the
 *  database, with the times the 10-minute sweep will act on. */
export interface BookingChat {
  state: BookingChatState;
  /** Predicted, to the sweep's 10-minute resolution. */
  joins_at: string | null;
  leaves_at: string | null;
  left_confirmed_at: string | null;
  windowed: boolean;
  permanent: boolean;
  muted: boolean;
  excluded: { chat: boolean; group_chat: boolean };
  channel: { id: number; starts_at: string | null; ends_at: string | null } | null;
  channel_count: number;
  window: { add_before_minutes: number | null; remove_after_minutes: number | null };
  conversations: Array<{
    type: 'announcements' | 'guides_and_staff' | 'group_chat' | 'chat_with_user';
    participant_status: 'active_synced' | 'active_unsynced' | 'inactive' | 'removed' | 'deleted';
    count: number;
  }>;
}

/** The opt-in row expansions of `GET /bookings/?expand=` (one batched resolver per page
 *  each — ask only for what is shown). */
export type BookingListExpansion = 'guides' | 'where' | 'staff_visibility' | 'chat';

/** Their canonical order, which the request follows. */
export const BOOKING_LIST_EXPANSIONS: readonly BookingListExpansion[] = [
  'guides',
  'where',
  'staff_visibility',
  'chat',
];

/** One of the booking user's effective access groups (see the `agroup`
 *  filter namespace — direct membership, role-inherited, or the tenant guest
 *  group for users with no staff record). */
export interface BookingAccessGroupRef {
  id: number;
  name: string;
}

export interface BookingListItem {
  id: number;
  tenant: BookingTenantRef;
  tour: BookingTourRef;
  guest: BookingGuest | null;
  date: string | null;
  start_ts: string | null;
  /** The computed end (the end-time seam), never the raw column; null without a start. */
  end_ts: string | null;
  /** 0 = start + the tour's length; 1 = stored on the booking ("ends per booking"). */
  end_ts_type: 0 | 1;
  end_ts_type_display: string;
  all_day: boolean | null;
  /** end_ts − start_ts in seconds; null when either is null. */
  duration_s: number | null;
  /** The tour's IANA zone to read start_ts / end_ts in; null when none is known. */
  timezone: string | null;
  timezone_known: boolean;
  phase: BookingTripPhase;
  departure: BookingDepartureRef | null;
  booking_state: number;
  booking_state_display: string;
  people_count: number | null;
  custom_id: string | null;
  /** The booking's second id in its booking system, lowercase (Ventrata's native id
   *  while custom_id holds the uuid); null when the system has none. */
  alt_custom_id: string | null;
  order_ref: string | null;
  /** The order the booking belongs to; null when none. */
  order_id: number | null;
  /** The operator's id for the departure INSTANCE (distinct from `departure`,
   *  which is a time-of-day tag). Empty when the operator has none. */
  departure_key: string;
  supplier_ref: string | null;
  reseller_ref: string | null;
  affiliate_ext_id: string | null;
  /** Staff role on the booking. Null for ordinary guest bookings. */
  role: BookingRoleRef | null;
  participant_kind: BookingParticipantKind;
  /** Effective access groups of the booking's user; `[]` when there is no user. */
  agroups: BookingAccessGroupRef[];
  tags: BookingTag[];
  integration: string | null;
  /** Booking timestamp (the guest's booked-at instant; visible column). */
  booking_ts: string | null;
  /** Raw Group_Tour_User.SURVEY_STATES value — the Survey column renders the
   *  `_display` label instead (showing this int was a bug). */
  survey_state: number;
  /** Post-trip survey state label (visible column). */
  survey_state_display: string;
  /** Stable public booking identifier (hidden by default). */
  public_id: string | null;
  /** Branch.io deep link to the booking (hidden by default). */
  branchio_link: string | null;
  /** Whether the booking shows in the guest's "My Trips" (hidden by default). */
  mytrips_display: boolean;
  /** Chat exclusions (hidden by default). */
  exclude_from_chat: boolean;
  exclude_from_group_chat: boolean;
  created_ts: string | null;
  /** Human-readable Group_Tour_User.SOURCES label (e.g. "Dashboard"). */
  create_source_display: string;
  updated_ts: string | null;
  update_source_display: string;

  // ---- opt-in expansions: present only when `expand=` asked for them ------
  guides?: BookingGuides;
  where?: BookingWhere;
  /** Null on guest and observer rows. */
  staff_visibility?: BookingStaffVisibility | null;
  chat?: BookingChat;
}

/** The list endpoint's paginated envelope carries a capped-count flag. */
export type BookingListResponse = PaginatedResponse<BookingListItem> & {
  /** Present in `count_mode=sync` only: the sync count is capped at 10 000
   *  probed rows. The async/deferred on-demand total is exact and uncapped, so
   *  there is nothing to cap and the key is absent. */
  count_is_capped?: boolean;
};

export interface BookingListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default (`deferred` on this grid) — that omission is what keeps a cold
   *  first load free of a COUNT. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
  /** Opt-in row expansions (sent in canonical order; omitted when empty). */
  expand?: BookingListExpansion[];
}

function toQuery(p: BookingListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  if (p.count_mode) usp.set('count_mode', p.count_mode);
  if (p.count_only) usp.set('count_only', '1');
  // A total needs no row expansions.
  const expand = p.count_only ? [] : BOOKING_LIST_EXPANSIONS.filter((e) => p.expand?.includes(e));
  if (expand.length > 0) usp.set('expand', expand.join(','));
  return usp.toString();
}

export async function list(p: BookingListParams = {}): Promise<BookingListResponse> {
  const qs = toQuery(p);
  return apiGet(`/bookings/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** Total only, for the grid's on-demand row count. `async` so the count runs
 *  in Celery: an exact, uncapped total that never occupies a web worker. */
export async function count(p: BookingListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...p, count_only: true, count_mode: 'async' });
  return apiGet(`/bookings/?${qs}`);
}

// ---------------------------------------------------------------------------
// Booking detail + read-only sub-resources (overview panel / master-detail).
// Shared refs (NamedRef/UserRef/…) are re-exported for api/orders.ts.
// ---------------------------------------------------------------------------

/** `{id, name}` brief for a tour / departure. */
export interface NamedRef {
  id: number;
  name: string | null;
}

/** `{id, first_name, last_name}` for a guest/participant. */
export interface UserRef {
  id: number;
  first_name: string | null;
  last_name: string | null;
}

/** A booking attachment / voucher — name + full file URL + short link. */
export interface BookingAttachmentRef {
  name: string | null;
  url: string | null;
  short_url: string | null;
}

export interface BookingGuestContact {
  type: number;
  type_display: string;
  tier_display: string;
  contact: string | null;
  verified: boolean;
}

export interface BookingGuestDetail {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  lang: string | null;
  date_joined: string | null;
  last_login: string | null;
  contacts: BookingGuestContact[];
  /** The online travel agency the guest's email belongs to (null when none). */
  ota?: { name: string | null; domain: string | null; code: string | null; email: string | null } | null;
  /** users.User.platform — the apps / platforms the guest has used, as stored. */
  platform?: unknown;
  /** The guest's chat (Twilio) identity. */
  twilio_identity?: string | null;
}

/** ConsoleBookingTourRef — a booking's tour with the zone its schedule is read in. */
export interface BookingDetailTourRef {
  id: number;
  name: string | null;
  custom_id?: string | null;
  /** IANA zone every local date/time of the booking is shown and edited in. */
  start_timezone?: string | null;
  /** A bespoke tour's bookings take the tour's own start. */
  is_bespoke?: boolean;
  start_date?: string | null;
  start_ts?: string | null;
}

/** One configured pre-check-in field with the submitted value (get_precheckin_fields). */
export interface BookingPrecheckinField {
  id: string;
  label: string;
  type: string | null;
  required: boolean;
  scope: 'lead' | 'party';
  populated: boolean;
  value?: unknown;
  display?: string | null;
}

/** The booking's pre-check-in as the guest sees it (get_precheckin_summary). */
export interface BookingPrecheckinSummary {
  enabled: boolean;
  /** The tour uses the operator-level pre-check-in settings. */
  inherited: boolean;
  status?: number;
  status_label?: string;
  fields: BookingPrecheckinField[];
  /** Main booking first, then its sub-bookings. */
  participants: Array<{
    gtu: number;
    name?: string;
    email?: string;
    role: 'lead' | 'party';
    /** Pre-check-in completed (not the on-site check-in). */
    checked_in: boolean;
    skipped: boolean;
    fields: BookingPrecheckinField[];
  }>;
}

/** One signed waiver (Waiver_Signature) — legal evidence, never edited. */
export interface BookingWaiverSignature {
  id: number;
  waiver: NamedRef | null;
  /** The signing participant's booking id. */
  participant_id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  lang?: string | null;
  agreed: boolean;
  created_ts: string;
}

/** One extra booked with the booking (Booking_Extra). Prices in minor units. */
export interface BookingExtra {
  id: number;
  extra_id: string;
  title: string;
  description?: string | null;
  quantity: number;
  ticket_id?: number | null;
  currency?: string | null;
  currency_precision?: number | null;
  price_net?: number | null;
  price_original?: number | null;
  price_retail?: number | null;
  tax_amount?: number | null;
}

/** One review the guest submitted for the booking (User_Tour_Review). */
export interface BookingReview {
  id: number;
  star_rating?: number | null;
  comment?: string | null;
  link?: string | null;
  created_ts: string | null;
}

export interface BookingTicket {
  id: number;
  /** True when the row belongs to the booking's main booking, not to this booking:
   *  sub-bookings own no tickets and display the lead's. Only ever true when the
   *  operator has `inherit_tickets_from_main_booking` on. */
  inherited: boolean;
  /** The booking that owns the ticket (its `main_booking`). */
  owner_booking_id: number | null;
  ticket_id: string | null;
  label: string | null;
  format: number;
  format_display: string;
  unit: number;
  unit_display: string;
  sequence: number;
  redeemed_at: string | null;
  noshowed_at: string | null;
  use_count: number;
  /** Exposed but hidden by default in the grid. */
  value: string | null;
  remote_value: string | null;
  file_url: string | null;
  data: unknown;
  currency: string | null;
  currency_precision: number | null;
  price_net: number | null;
  price_retail: number | null;
  created_ts: string;
  updated_ts: string;
}

export interface BookingFamilyNode {
  id: number;
  /** main_booking id (tree parent), null at the root. */
  parent_id: number | null;
  is_current: boolean;
  user: UserRef | null;
  contacts: { email: string | null; phone: string | null };
  registration_method: string;
  registration_ts: string | null;
  last_login: string | null;
  tags: BookingTag[];
}

export interface PackageBooking {
  id: number;
  is_current: boolean;
  tour: NamedRef | null;
  date: string | null;
  start_ts: string | null;
  departure: NamedRef | null;
  user: UserRef | null;
  tags: BookingTag[];
}

/** GET /bookings/{id}/ — ConsoleBookingDetail (contract test:
 *  bookingDetail.contract.test.ts). The record-editor read model (2026-10-01) added the
 *  optional fields below the core ones; they are optional because the spec does not
 *  require them. */
export interface BookingDetail {
  id: number;
  tenant: BookingTenantRef | null;
  tour: BookingDetailTourRef | null;
  guest: BookingGuestDetail | null;
  departure: NamedRef | null;
  /** The staff role on this booking; null for an ordinary guest booking. */
  role?: NamedRef | null;
  date: string | null;
  start_ts: string | null;
  /** Computed, not the stored column: start_ts + tour length for an itinerary
   *  booking, the booking's own check-out for a stay. `end_ts_type` says which. */
  end_ts: string | null;
  end_ts_type: number;
  end_ts_type_display: string;
  booking_ts: string | null;
  people_count: number | null;
  tag_context: Record<string, unknown> | null;
  checkin_source: number;
  checkin_source_display: string;
  checkin_ts: string | null;
  precheckin_status: number;
  survey_state: number;
  survey_state_display: string;
  booking_state: number;
  booking_state_display: string;
  order_ref: string | null;
  order_id: number | null;
  custom_id: string | null;
  /** The booking's second id in its booking system (Ventrata's native id); null when none. */
  alt_custom_id: string | null;
  prev_custom_id: string | null;
  product_custom_id: string | null;
  departure_key: string;
  /** A human set the departure code; the importer must not overwrite it. */
  departure_key_locked: boolean;
  public_id: string | null;
  supplier_ref: string | null;
  reseller_ref: string | null;
  affiliate_ext_id: string | null;
  parent_custom_id: string | null;
  itinerary_id: number | null;
  created_ts: string | null;
  create_source: number;
  create_source_display: string;
  updated_ts: string | null;
  update_source: number;
  update_source_display: string;
  ext_updated_ts: string | null;
  integration: string | null;
  restech_account: string | null;
  booking_system: number;
  booking_system_display: string;
  branchio_link: string | null;
  voucher: BookingAttachmentRef | null;
  attachments: BookingAttachmentRef[];
  guide_sr_link: string | null;
  /** The guide link is revoked (it still shows, but no longer works); false when none (Plan B: no reader yet). */
  guide_sr_link_revoked?: boolean;
  sr_link: string | null;
  /** The link the guest registered through is revoked since; false when none. */
  sr_link_revoked?: boolean;
  mytrips_display: boolean;
  exclude_from_chat: boolean;
  exclude_from_group_chat: boolean;
  pause_all_notifications: boolean;
  pause_pre_and_post_trip_notifications: boolean;
  main_booking: { id: number; user: UserRef | null } | null;
  parent_booking_id: number | null;
  package_id: string | null;
  package_title: string | null;
  currency: string | null;
  currency_precision: number | null;
  price_net: number | null;
  price_original: number | null;
  price_retail: number | null;
  tax_amount: number | null;
  tags: BookingTag[];
  tickets: BookingTicket[];
  family: BookingFamilyNode[];
  package: PackageBooking[];

  // ---- the record-editor read model -------------------------------------
  /** The raw `end_ts` column — authoritative only when `end_ts_type` = 1. */
  stored_end_ts?: string | null;
  all_day?: boolean | null;
  /** From tag_context (adult_count / adults_count, …). */
  adult_count?: number | null;
  children_count?: number | null;
  infant_count?: number | null;
  checkin_status?: BookingRecordCheckinStatus;
  checkin_error?: string | null;
  /** Extranet (interactive) check-in link. */
  checkin_url?: string | null;
  checkin_notes?: string | null;
  /** PRECHECKIN_STATES label of the party aggregate. */
  precheckin_status_display?: string;
  /** This participant's OWN pre-check-in state. */
  precheckin_state?: BookingRecordPrecheckinState;
  is_minor?: boolean;
  emergency_contact?: string | null;
  custom_note?: string | null;
  precheckin?: BookingPrecheckinSummary | null;
  /** The party's waiver signatures, newest first; `signed` counts them. */
  waivers?: { signed: number; signatures: BookingWaiverSignature[] };
  booking_notes?: string | null;
  invoice?: BookingAttachmentRef | null;
  extras?: BookingExtra[];
  /** The guest's booking-management link. It logs the guest in, so only a masked form
   *  is returned; revealing it is an audited action. */
  bookingmgmt_link?: { available: boolean; masked: string | null };
  /** The post-trip review page (masked — it lets the holder review as the guest) and
   *  the reviews submitted for this booking, newest first. */
  reviews?: { available: boolean; masked: string | null; items: BookingReview[] };
  package_description?: string | null;
  promo_code?: string | null;
  /** Minor units. */
  promo_discount?: number | null;
  settlement_method?: string | null;
  email_receipt?: boolean | null;
}

/** The booking detail's query key — BookingDetailPage, the grid's master-detail panel
 *  and the record editor's apply (which re-seeds it from the response) share it. */
export const bookingDetailKey = (id: number) => ['bookings', 'detail', id] as const;

// Import runs now share the /import_runs/ grid row shape — see
// api/importRuns.ts (ImportRunListItem).

export interface DeliveryLogNotification {
  id: number | null;
  notification_id: number | null;
  notification_name: string | null;
  delivery_method: number | null;
  delivery_method_name: string | null;
  /** The method's status (Event_Delivery_Notification_Log.STATUSES; 6–8 a sandbox's). */
  status?: number | null;
  n_success: number | null;
  n_error: number | null;
}

export interface DeliveryLog {
  id: string;
  event: string | null;
  log_id: string | null;
  log_tag: string | null;
  created_ts: string | null;
  finalized_ts: string | null;
  expires_ts: string | null;
  duration_ms: number | null;
  status: number | null;
  status_display: string | null;
  rendering_lang: string | null;
  order_ref: string | null;
  /** The booking-SYSTEM id (`Event_Delivery_Log.booking_id` is a CharField —
   *  the `booking=` namespace on /delivery-logs), not the console pk. */
  booking_id: string | null;
  n_notifications: number | null;
  n_success: number | null;
  n_error: number | null;
  /** Delivery methods a sandbox captured; 0 on every production row. */
  n_captured?: number | null;
  archive_s3_key: string | null;
  notifications: DeliveryLogNotification[];
}

/** GET /bookings/{id}/ — rich detail powering the Booking overview tab / panel. */
export async function retrieve(id: number): Promise<BookingDetail> {
  return apiGet(`/bookings/${id}/`);
}

/** GET /bookings/{id}/imports/ */
export async function listImportRuns(id: number): Promise<ImportRunListItem[]> {
  return apiGet(`/bookings/${id}/imports/`);
}

/** GET /bookings/{id}/api_requests/ — the webhook / API requests that touched
 *  this booking (the /api_requests/ grid's row shape). */
export async function listApiRequests(id: number): Promise<ApiRequestListItem[]> {
  return apiGet(`/bookings/${id}/api_requests/`);
}

/** GET /bookings/{id}/delivery_logs/ */
export async function listDeliveryLogs(id: number): Promise<DeliveryLog[]> {
  return apiGet(`/bookings/${id}/delivery_logs/`);
}

export type SuggestKind = 'tenant' | 'tour' | 'user' | 'tag' | 'category' | 'role' | 'access_group';

/** The autocomplete path uses pluralised kind names. */
const KIND_PATH: Record<SuggestKind, string> = {
  tenant: 'tenants',
  tour: 'tours',
  user: 'users',
  tag: 'tags',
  category: 'categories',
  role: 'roles',
  access_group: 'access_groups',
};

/** Present on every non-tenant autocomplete row — an unscoped query (no
 *  tour_operator_id) can return same-named rows (a tag, a tour, a guest)
 *  from different tenants, and this is the only way to distinguish them in
 *  the suggestion list. Null when the underlying row has no tour_operator
 *  (rare — the FK is nullable on Group_Tour/Group_Tour_Tag/User). */
type TenantRef = {
  code: string;
  name: string;
  environment?: TourOperatorEnvironment | null;
} | null;

interface TenantSuggest { id: number; code: string; name: string }
interface TourSuggest { id: number; name: string; tenant: TenantRef }
interface UserSuggest {
  id: number;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  tenant: TenantRef;
}
interface TagSuggest { id: number; name: string; category: BookingTagCategory; tenant: TenantRef }
interface CategorySuggest { id: number; name: string; code: string; tenant: TenantRef }
interface RoleSuggest { id: number; name: string; tenant: TenantRef }
interface AccessGroupSuggest { id: number; name: string; codename: string | null; tenant: TenantRef }
type SuggestRow =
  | TenantSuggest
  | TourSuggest
  | UserSuggest
  | TagSuggest
  | CategorySuggest
  | RoleSuggest
  | AccessGroupSuggest;

/** Owner candidate row from `/bookings/autocomplete/users/` — the raw shape the
 *  batch "Change owner" dialog picks from (the QuerySearch fetcher above maps
 *  these to `Suggestion`s, dropping the id we need here). */
export interface BookingUserOption {
  id: number;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  tenant: TenantRef;
}

/** Tag candidate row from `/bookings/autocomplete/tags/`. */
export interface BookingTagOption {
  id: number;
  name: string;
  category: BookingTagCategory;
  tenant: TenantRef;
}

function autocompleteQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  return usp.toString();
}

/** Owner candidates for a tenant (batch "Change owner"). Scoped to
 *  `tour_operator_id` so the picked owner's tenant is known (== that id). */
export async function ownerOptions(
  tourOperatorId: number,
  q: string,
  signal?: AbortSignal,
): Promise<BookingUserOption[]> {
  const qs = autocompleteQuery({ q, tour_operator_id: tourOperatorId, limit: 20 });
  const res = await apiGet<{ results: BookingUserOption[] }>(
    `/bookings/autocomplete/users/?${qs}`,
    signal ? { signal } : undefined,
  );
  return res.results;
}

/** Tag candidates for a tenant (batch add/remove tags). Scoped to
 *  `tour_operator_id`; `category` (code) narrows by tag category when set. */
export async function tagOptions(
  tourOperatorId: number,
  q: string,
  category?: string,
  signal?: AbortSignal,
): Promise<BookingTagOption[]> {
  const qs = autocompleteQuery({ q, tour_operator_id: tourOperatorId, category, limit: 20 });
  const res = await apiGet<{ results: BookingTagOption[] }>(
    `/bookings/autocomplete/tags/?${qs}`,
    signal ? { signal } : undefined,
  );
  return res.results;
}

/** Tag-category candidates for a tenant (narrows the tag picker by category). */
export async function categoryOptions(
  tourOperatorId: number,
  q: string,
  signal?: AbortSignal,
): Promise<BookingTagCategory[]> {
  const qs = autocompleteQuery({ q, tour_operator_id: tourOperatorId, limit: 20 });
  const res = await apiGet<{ results: BookingTagCategory[] }>(
    `/bookings/autocomplete/categories/?${qs}`,
    signal ? { signal } : undefined,
  );
  return res.results;
}

/** Build a QuerySearch-compatible fetcher for an autocomplete kind. */
export function makeSuggestionFetcher(
  kind: SuggestKind,
  staticExtra?: Record<string, string>,
): SuggestionFetcher {
  return async (query, extraFilters, signal) => {
    const usp = new URLSearchParams();
    if (query) usp.set('q', query);
    usp.set('limit', '15');
    const merged = { ...(staticExtra ?? {}), ...extraFilters };
    for (const [k, v] of Object.entries(merged)) {
      if (v) usp.set(k, v);
    }
    const res = await apiGet<{ results: SuggestRow[] }>(
      `/bookings/autocomplete/${KIND_PATH[kind]}/?${usp.toString()}`,
      { signal },
    );
    return res.results.map((row) => toSuggestion(kind, row));
  };
}

/** Appends the tenant code to a suggestion's description so same-named rows
 *  from different tenants (a tag, a tour, a guest) stay distinguishable in
 *  an unscoped ("All tenants") suggestion list. */
function appendTenant(description: string | undefined, tenant: TenantRef): string | undefined {
  if (!tenant) return description;
  return description ? `${description} · ${tenant.code}` : tenant.code;
}

/** Exported for direct unit testing (see bookings.test.ts) — the row→Suggestion
 *  mapping, including the tenant-disambiguation logic, has no other test path
 *  since makeSuggestionFetcher's own debounce/abort behavior is already
 *  covered via QuerySearchInput.test.tsx with a hand-rolled fetcher. */
export function toSuggestion(kind: SuggestKind, row: SuggestRow) {
  switch (kind) {
    case 'tenant': {
      const r = row as TenantSuggest;
      return { value: r.code, label: r.name, description: r.code };
    }
    case 'tour': {
      const r = row as TourSuggest;
      return { value: r.name, label: r.name, description: appendTenant(undefined, r.tenant), id: r.id };
    }
    case 'user': {
      const r = row as UserSuggest;
      const name = `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim();
      const value = r.email || r.phone || '';
      return { value, label: name || value, description: appendTenant(r.email ?? undefined, r.tenant), id: r.id };
    }
    case 'tag': {
      const r = row as TagSuggest;
      return { value: r.name, label: r.name, description: appendTenant(r.category?.name, r.tenant), id: r.id };
    }
    case 'category': {
      const r = row as CategorySuggest;
      // `code` is the canonical filter value (e.g. category=departure) —
      // shown in the description too since it's what actually gets filled
      // in, and may differ from the display-cased name. `id` powers "by ID".
      return { value: r.code, label: r.name, description: appendTenant(r.code, r.tenant), id: r.id };
    }
    case 'role': {
      const r = row as RoleSuggest;
      return { value: r.name, label: r.name, description: appendTenant(undefined, r.tenant), id: r.id };
    }
    case 'access_group': {
      const r = row as AccessGroupSuggest;
      // Groups are filtered by NAME (what the grid chip shows); the codename
      // rides in the description purely to disambiguate same-named groups.
      return {
        value: r.name,
        label: r.name,
        description: appendTenant(r.codename ?? undefined, r.tenant),
        id: r.id,
      };
    }
  }
}

// ---- AI diagnostics (perm: console_entity_ai) -------------------------------
// One entity_diagnose tool serves bookings, orders AND users; types live in
// features/ai-diag.

export async function diagnoseLaunchContext(): Promise<DiagnoseLaunchContext> {
  return apiGet('/bookings/diagnose/launch_context/');
}

/** POST /bookings/{id}/diagnose/ — start an AI diagnostic run (or a follow-up
 *  when `thread_task_id` is set). 202 { task_id } — poll bgtasks; 409 carries
 *  the already-running task_id (adopt it). */
export async function startDiagnose(
  id: number,
  body: AiDiagStartBody,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/bookings/${id}/diagnose/`, body);
}
